<script setup>
import VueDatePicker from '@vuepic/vue-datepicker';
import { usePage } from '@inertiajs/vue3';
import { formatDate } from '@/helpers.js';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
    min: undefined,
    position: {
        type: String,
        default: 'right',
    },
    options: {
        type: Object,
        default: () => ({
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        }),
    },
    name: {
        type: String,
        default: () => 'date',
    },
});

const date = ref(new Date(props.min));

const datePickContainer = ref(null);

const i18n = useI18n();
const emit = defineEmits(['change']);

const flow = ref(['calendar', 'time']);

function toDateKey(date) {
    return [
        date.getFullYear(),
        String(date.getMonth() + 1).padStart(2, '0'),
        String(date.getDate()).padStart(2, '0'),
    ].join('-');
}

function getDisabledDateRange(startDate, endDate) {
    const dates = [];
    const currentDate = new Date(startDate);

    while (currentDate <= endDate) {
        dates.push(toDateKey(currentDate));
        currentDate.setDate(currentDate.getDate() + 1);
    }

    return dates;
}

// Parsed as a local date, so a 'YYYY-MM-DD' string is not shifted a day by the UTC offset.
function fromDateKey(value) {
    const [year, month, day] = value.split('-').map(Number);

    return new Date(year, month - 1, day);
}

const page = usePage();

const format = (date) => {
    return formatDate(date, props.options, i18n?.locale?.value);
};
const changeDate = () => {
    emit('change', date);
};
// INIT the date
changeDate();

// Shared by HandleInertiaRequests from Reservation::WINTER_CLOSURE_START / WINTER_CLOSURE_END.
const disabledDates = computed(() => {
    const winterClosure = page.props.config?.winterClosure;

    // Fail loudly instead of falling back to an empty list: without these dates the picker
    // would happily offer days the parking area is closed, and the visitor would only find
    // out when the reservation is rejected two steps later.
    if (!winterClosure?.start || !winterClosure?.end) {
        throw new Error(
            'DatePicker is missing the config.winterClosure prop. HandleInertiaRequests must share Reservation::WINTER_CLOSURE_START and WINTER_CLOSURE_END.',
        );
    }

    return getDisabledDateRange(
        fromDateKey(winterClosure.start),
        fromDateKey(winterClosure.end),
    );
});

const scrollIntoView = () => {
    datePickContainer.value.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
    });
};
</script>

<template>
    <section ref="datePickContainer" class="w-full text-xs input__date-picker">
        <VueDatePicker
            ref="datePicker"
            v-model="date"
            :name="name"
            :flow="flow"
            minutes-increment="15"
            minutes-grid-increment="15"
            :format="format"
            :clearable="false"
            :min-date="min"
            :disabled-dates="disabledDates"
            :action-row="{ showPreview: false, showCancel: false }"
            @closed="changeDate"
            @open="scrollIntoView"
        >
            <template #action-row="{ selectDate }">
                <div class="action-row w-full">
                    <button
                        type="button"
                        class="w-full btn btn-primary"
                        @click="selectDate"
                    >
                        Bestätigen
                    </button>
                </div>
            </template>
        </VueDatePicker>
    </section>
</template>
